/**
 * ExportPanel — Multi-format export and publishing.
 *
 * Composes:
 * - outputtr-lib (OutputtrManager): export(), report generation
 * - convertr-lib (MarkdownToDocxPlugin): Markdown → DOCX conversion
 * - keepr-lib (KeeprManager): Publish to permanent knowledge store
 * - fylr-lib (Fylr): File export to external sources
 * - selectr-ui (Selectr): Format picker
 * - widgetr-lib (Button): Action buttons
 *
 * The panel delegates export orchestration to the appropriate package.
 * Direct text downloads are handled in-browser; DOCX uses convertr-lib;
 * complex formats (PDF) route through outputtr/publishr.
 */

import { useState, useCallback } from 'react';
import { useExpressrContext } from '../context/ExpressrContext.js';

/** Export format definitions — would be selectr-ui items */
const EXPORT_FORMATS = [
  { id: 'markdown', label: 'Markdown', description: 'Standard Markdown (.md)', mimeType: 'text/markdown', extension: '.md' },
  { id: 'html', label: 'HTML', description: 'Web-ready HTML (.html)', mimeType: 'text/html', extension: '.html' },
  { id: 'plain', label: 'Plain Text', description: 'Plain text (.txt)', mimeType: 'text/plain', extension: '.txt' },
  { id: 'docx', label: 'Word Document', description: 'Via convertr-lib (.docx)', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: '.docx' },
  { id: 'pdf', label: 'PDF', description: 'Portable Document Format', mimeType: 'application/pdf', extension: '.pdf' },
  { id: 'json', label: 'JSON', description: 'Structured JSON export', mimeType: 'application/json', extension: '.json' },
];

export function ExportPanel() {
  const { activeDocument, outputtr, exportDocument, publishToKeepr, exportViaFylr, config } = useExpressrContext();
  const [lastExport, setLastExport] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [keeprStatus, setKeeprStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [fylrStatus, setFylrStatus] = useState<'idle' | 'exported' | 'error'>('idle');

  const handleCopy = useCallback(async () => {
    if (!activeDocument) return;
    try {
      await navigator.clipboard.writeText(activeDocument.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = activeDocument.content;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [activeDocument]);

  /** Download as blob in the browser */
  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleDownload = useCallback(
    async (formatId: string) => {
      if (!activeDocument) return;
      const format = EXPORT_FORMATS.find((f) => f.id === formatId);
      if (!format) return;
      const safeName = activeDocument.title.replace(/[^a-zA-Z0-9-_ ]/g, '');

      const isDirectDownload = ['markdown', 'html', 'plain'].includes(formatId);

      if (isDirectDownload) {
        let content = activeDocument.content;
        if (formatId === 'html' && activeDocument.format === 'markdown') {
          content = `<!DOCTYPE html>\n<html>\n<head>\n  <meta charset="utf-8">\n  <title>${activeDocument.title}</title>\n</head>\n<body>\n<h1>${activeDocument.title}</h1>\n${content}\n</body>\n</html>`;
        }
        downloadBlob(new Blob([content], { type: format.mimeType }), `${safeName}${format.extension}`);
        setLastExport(formatId);
      } else if (formatId === 'docx') {
        // DOCX via convertr-lib's MarkdownToDocxPlugin.
        // In full integration: import { markdownToDocx } from '@opcentral/convertr-lib';
        // For now: delegate to onPublish which can wire the converter.
        await exportDocument('docx');
        setLastExport(formatId);
      } else if (formatId === 'json') {
        const exported = JSON.stringify({
          title: activeDocument.title,
          content: activeDocument.content,
          format: activeDocument.format,
          status: activeDocument.status,
          tags: activeDocument.tags,
          wordCount: activeDocument.wordCount,
          exportedAt: new Date().toISOString(),
        }, null, 2);
        downloadBlob(new Blob([exported], { type: format.mimeType }), `${safeName}${format.extension}`);
        setLastExport(formatId);
      } else {
        // Complex formats — delegate to outputtr-lib / config.onPublish
        await exportDocument(formatId);
        setLastExport(formatId);
      }
    },
    [activeDocument, exportDocument, downloadBlob]
  );

  /** Publish to keepr — permanent knowledge store */
  const handlePublishToKeepr = useCallback(async () => {
    if (!activeDocument) return;
    setKeeprStatus('saving');
    try {
      const result = await publishToKeepr('document', activeDocument.tags);
      setKeeprStatus(result.success ? 'saved' : 'error');
      if (result.success) {
        setTimeout(() => setKeeprStatus('idle'), 3000);
      }
    } catch {
      setKeeprStatus('error');
    }
  }, [activeDocument, publishToKeepr]);

  /** Export via fylr — file management */
  const handleExportViaFylr = useCallback(() => {
    if (!activeDocument) return;
    try {
      exportViaFylr();
      setFylrStatus('exported');
      setTimeout(() => setFylrStatus('idle'), 3000);
    } catch {
      setFylrStatus('error');
    }
  }, [activeDocument, exportViaFylr]);

  if (!activeDocument) {
    return (
      <div style={{ padding: 16, fontSize: 12, opacity: 0.6, height: '100%' }}>
        Select a document to export.
      </div>
    );
  }

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, opacity: 0.6 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Export &amp; Publish</span>
        <div style={{ fontSize: 11, marginTop: 2, opacity: 0.6 }}>
          &ldquo;{activeDocument.title}&rdquo; ({activeDocument.wordCount} words)
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        {/* Quick actions — would use widgetr Button */}
        <div style={{ marginBottom: 16 }}>
          <span style={labelStyle}>Quick Actions</span>
          <button onClick={handleCopy} style={{ width: '100%', padding: '8px 12px', textAlign: 'left', marginBottom: 6 }}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>

        {/* Export formats — would use selectr-ui Selectr for format picker */}
        <div>
          <span style={labelStyle}>Download As</span>
          {EXPORT_FORMATS.map((format) => {
            const needsPublisher = format.id === 'pdf';
            const needsConvertr = format.id === 'docx';
            const hasPublisher = !!config?.onPublish;

            return (
              <button
                key={format.id}
                onClick={() => handleDownload(format.id)}
                disabled={needsPublisher && !hasPublisher}
                data-active={lastExport === format.id}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  textAlign: 'left',
                  marginBottom: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  opacity: needsPublisher && !hasPublisher ? 0.5 : 1,
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{format.label}</div>
                  <div style={{ fontSize: 11, opacity: 0.6 }}>
                    {needsPublisher && !hasPublisher
                      ? 'Requires publishr integration'
                      : needsConvertr
                        ? 'Via convertr-lib MarkdownToDocxPlugin'
                        : format.description}
                  </div>
                </div>
                {lastExport === format.id && (
                  <span style={{ marginLeft: 'auto', fontSize: 11 }}>Exported</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Keepr — permanent knowledge storage */}
        <div style={{ marginTop: 16 }}>
          <span style={labelStyle}>Save to Library (keepr)</span>
          <button
            onClick={handlePublishToKeepr}
            disabled={keeprStatus === 'saving'}
            style={{ width: '100%', padding: '10px 12px', textAlign: 'left' }}
          >
            <div style={{ fontWeight: 500 }}>
              {keeprStatus === 'saving' ? 'Publishing...'
                : keeprStatus === 'saved' ? 'Published to Keepr'
                : keeprStatus === 'error' ? 'Publish Failed'
                : 'Publish to Knowledge Library'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
              Permanent storage via keepr-lib. Searchable, versioned, verified.
            </div>
          </button>
        </div>

        {/* Fylr — file export */}
        <div style={{ marginTop: 16 }}>
          <span style={labelStyle}>File Export (fylr)</span>
          <button
            onClick={handleExportViaFylr}
            style={{ width: '100%', padding: '10px 12px', textAlign: 'left' }}
          >
            <div style={{ fontWeight: 500 }}>
              {fylrStatus === 'exported' ? 'Exported via Fylr'
                : fylrStatus === 'error' ? 'Export Failed'
                : 'Export to File System'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
              Managed file output via fylr-lib. Version tracked, shareable.
            </div>
          </button>
        </div>

        {/* Pipeline info */}
        <div style={{ marginTop: 16, padding: 12, borderRadius: 4, opacity: 0.7 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>CREATIVE PIPELINE</span>
          <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>
            expressr (write) → editr (refine) → publishr (layout) → delivvr (distribute)
          </div>
          <div style={{ fontSize: 10, marginTop: 4, lineHeight: 1.5 }}>
            keepr = permanent library | fylr = managed files | mattr = event tracking
          </div>
        </div>
      </div>
    </div>
  );
}
